// @ts-nocheck
/**
 * Compile cache for descriptor pipeline IR.
 *
 * Keyed by the pipeline *input* document identity (the registry object), not
 * `loadTemplate`'s clone. Prop-only overwrites (`text.editable`, styles, …)
 * replace that object via `normalizeDocument` and must recompile.
 *
 * Lives in its own module so `TemplateRuntimeRegistry` can invalidate on
 * register/clear without importing DescriptorPipeline (BindingResolver cycle).
 */

import type { ValidationIssue } from '../../diagnostics';
import type { TemplateDocument } from '../../types/canonical';
import { getTemplateId } from '../../../utils/templateDocumentUtils';
import { compileTemplate } from '../compiler';
import type { CompiledRuntimeTemplate } from '../compiler';
import type { LoadedTemplate } from '../loader';

export interface CompileCacheEntry {
  compiled: CompiledRuntimeTemplate | null;
  issues: ValidationIssue[];
}

let compileTemplateCache = new WeakMap<TemplateDocument, CompileCacheEntry>();
/** Latest source document compiled per templateId — lets overwrite invalidate without walking WeakMap. */
const compileCacheSourceByTemplateId = new Map<string, TemplateDocument>();

export function compileTemplateCached(
  sourceDocument: TemplateDocument,
  loaded: LoadedTemplate,
): CompileCacheEntry {
  const cached = compileTemplateCache.get(sourceDocument);
  if (cached) {
    return cached;
  }
  const { compiled, issues } = compileTemplate(loaded);
  const entry: CompileCacheEntry = { compiled, issues };
  compileTemplateCache.set(sourceDocument, entry);
  compileCacheSourceByTemplateId.set(getTemplateId(sourceDocument), sourceDocument);
  return entry;
}

/**
 * Drop compiled IR for one template (overwrite / delete) or all templates.
 * Call from `TemplateRuntimeRegistry.register` / `clear` / `unregister`.
 */
export function invalidateCompileTemplateCache(templateId?: string): void {
  if (!templateId) {
    compileTemplateCache = new WeakMap();
    compileCacheSourceByTemplateId.clear();
    return;
  }
  const source = compileCacheSourceByTemplateId.get(templateId);
  if (source) {
    compileTemplateCache.delete(source);
    compileCacheSourceByTemplateId.delete(templateId);
  }
}
